/**
 * SSC ship check: do we ship to the visitor's country?
 *
 * Loaded once per page from layout/theme.liquid (import map `@theme/ssc-ship-check`, so the country
 * waitlist shares this instance). Ship-to countries are the store's market countries, written by
 * Liquid into <html data-ssc-ship-countries> (localization.available_countries), never hardcoded.
 * The country comes from `?ssc_geo=XX` (test override for the session, `?ssc_geo=off` resets),
 * else the session cache, else Shopify's browsing context suggestions. Any error, a 2 s timeout or
 * an unknown country counts as shippable, so nobody is ever blocked from buying by a failed check.
 * Not shipping there: <html data-ssc-no-ship="XX">, which hides quick add on cards (ssc-overrides)
 * and lets the PDP waitlist take over the buy area. "Shop anyway" clears it for the session.
 * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
 */
const DETECTED_KEY = 'ssc-detected-country';
const DISMISSED_KEY = 'ssc-waitlist-dismissed';
const OVERRIDE_KEY = 'ssc-geo-override';
const TIMEOUT_MS = 2000;

/** @param {string} key */
function readSession(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string | null} value - null removes the key
 */
function writeSession(key, value) {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

/** @param {string} code */
function countryName(code) {
  try {
    const names = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
    return names.of(code) || code;
  } catch {
    return code;
  }
}

/** @returns {string | null} An ISO code from ?ssc_geo= (kept for the session), or null */
function readOverride() {
  const param = new URLSearchParams(window.location.search).get('ssc_geo');
  if (param !== null) {
    const code = param.trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) {
      writeSession(OVERRIDE_KEY, code);
      // A new test country starts without an earlier "shop anyway"
      writeSession(DISMISSED_KEY, null);
      return code;
    }
    writeSession(OVERRIDE_KEY, null);
    return null;
  }
  return readSession(OVERRIDE_KEY);
}

/** @returns {Promise<string | null>} */
async function detectCountry() {
  const cached = readSession(DETECTED_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed?.code) return String(parsed.code);
    } catch {
      /* refetch */
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = await response.json();
    const handle = data?.detected_values?.country?.handle;
    if (!handle) return null;
    const code = String(handle).toUpperCase();
    writeSession(DETECTED_KEY, JSON.stringify({ code }));
    return code;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @typedef {{ code: string, name: string, shippable: boolean, dismissed: boolean }} ShipStatus
 * @returns {Promise<ShipStatus>}
 */
async function resolveStatus() {
  const shipTo = (document.documentElement.dataset.sscShipCountries ?? '').split(',').filter(Boolean);
  let code = readOverride();
  if (!code) {
    try {
      code = await detectCountry();
    } catch {
      code = null;
    }
  }
  if (!code || !shipTo.length) return { code: code ?? '', name: code ? countryName(code) : '', shippable: true, dismissed: false };
  return {
    code,
    name: countryName(code),
    shippable: shipTo.includes(code),
    dismissed: Boolean(readSession(DISMISSED_KEY)),
  };
}

/** Resolves once per page; never rejects */
export const shipStatus = resolveStatus().catch(() => ({ code: '', name: '', shippable: true, dismissed: false }));

shipStatus.then((status) => {
  if (!status.shippable && !status.dismissed) document.documentElement.dataset.sscNoShip = status.code;
});

/** "Shop anyway": buying is back for this session */
export function dismissShipCheck() {
  writeSession(DISMISSED_KEY, '1');
  delete document.documentElement.dataset.sscNoShip;
}
